/**
 * A CSV reader for rosters exported from somewhere else.
 *
 * Hand-written rather than pulled from a dependency, because the interesting part is small and the
 * failure modes are specific: a director exports from Acuity or Excel, and the file arrives with a
 * byte-order mark, Windows line endings, quoted fields containing commas, and — depending on the
 * machine's locale — semicolons instead of commas. Every one of those turns a working import into
 * "why is everyone called `Smith, Jane`".
 *
 * Pure and I/O-free, so it lives here and both the browser and the API can use it. The browser
 * parses for the mapping preview; the API never trusts that and validates the rows it is sent.
 */
export interface ParsedCsv {
  headers: string[];
  rows: string[][];
  /** What separated the fields. Surfaced so the UI can say so when it guesses wrong. */
  delimiter: string;
}

const DELIMITERS = [',', ';', '\t', '|'] as const;

/**
 * Guessed from the first line, by counting separators OUTSIDE quotes. Counting blindly picks the
 * comma in `"Nakamura, Ada"` and splits every name in the file.
 */
export function detectDelimiter(firstLine: string): string {
  let best = ',';
  let bestCount = 0;

  for (const candidate of DELIMITERS) {
    let count = 0;
    let inQuotes = false;

    for (let i = 0; i < firstLine.length; i += 1) {
      const char = firstLine[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === candidate && !inQuotes) {
        count += 1;
      }
    }

    if (count > bestCount) {
      best = candidate;
      bestCount = count;
    }
  }

  return best;
}

export function parseCsv(input: string, firstRowIsHeader = true): ParsedCsv {
  // Excel writes a BOM. Left in place it becomes part of the first header, so "Email" never
  // matches and the director is told their file has no email column. Written as an escape rather
  // than the character itself — invisible in an editor, and the lint rule rightly rejects it.
  const text = input.replace(/^\uFEFF/, '');

  const firstBreak = text.search(/\r?\n/);
  const firstLine = firstBreak === -1 ? text : text.slice(0, firstBreak);
  const delimiter = detectDelimiter(firstLine);

  const records: string[][] = [];
  let field = '';
  let record: string[] = [];
  let inQuotes = false;

  const endField = () => {
    record.push(field.trim());
    field = '';
  };

  const endRecord = () => {
    endField();
    // A trailing newline, or a row of nothing but separators, is not a person.
    if (record.some((value) => value.length > 0)) records.push(record);
    record = [];
  };

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];

    if (inQuotes) {
      if (char === '"') {
        // "" inside a quoted field is a literal quote, not the end of it.
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === delimiter) {
      endField();
    } else if (char === '\n') {
      endRecord();
    } else if (char === '\r') {
      // Swallowed; the \n that follows ends the record.
    } else {
      field += char;
    }
  }

  if (field.length > 0 || record.length > 0) endRecord();

  const [headers = [], ...rows] = records;
  return firstRowIsHeader ? { headers, rows, delimiter } : { headers: headers.map((_, i) => `Column ${i + 1}`), rows: records, delimiter };
}

/**
 * The fields the platform needs, and the header names a real export is likely to use for them.
 *
 * Matching is deliberate rather than clever: lowercase, strip everything that isn't a letter, then
 * compare. "First Name", "first_name" and "FIRSTNAME" are the same question.
 */
export type RosterField = 'firstName' | 'lastName' | 'email' | 'phone' | 'skillLevel' | 'paceOverride';

const SYNONYMS: Record<RosterField, string[]> = {
  firstName: ['firstname', 'first', 'givenname', 'fname', 'forename'],
  lastName: ['lastname', 'last', 'surname', 'familyname', 'lname'],
  email: ['email', 'emailaddress', 'mail', 'e'],
  phone: ['phone', 'phonenumber', 'mobile', 'cell', 'telephone', 'tel'],
  paceOverride: ['pace', 'playingpace'],
  skillLevel: ['skill', 'skilllevel', 'level', 'experience', 'ability'],
};

function normalise(header: string): string {
  return header.toLowerCase().replace(/[^a-z]/g, '');
}

/**
 * A first guess at which column is which, so the common case needs no clicking. The director can
 * always override it — the guess is a convenience, never a decision.
 */
export function guessColumnMapping(headers: string[]): Partial<Record<RosterField, number>> {
  const mapping: Partial<Record<RosterField, number>> = {};
  const used = new Set<number>();

  for (const [field, synonyms] of Object.entries(SYNONYMS) as [RosterField, string[]][]) {
    const index = headers.findIndex(
      (header, i) => !used.has(i) && synonyms.includes(normalise(header)),
    );
    if (index !== -1) {
      mapping[field] = index;
      used.add(index);
    }
  }

  /**
   * Some exports carry one "Name" column. Splitting on the last space is right far more often
   * than it is wrong ("Mary Anne Whitfield" → "Mary Anne" + "Whitfield"), and the director sees
   * the result in the preview before anything is imported.
   */
  if (mapping.firstName === undefined && mapping.lastName === undefined) {
    const nameIndex = headers.findIndex((header) => normalise(header) === 'name');
    if (nameIndex !== -1) mapping.firstName = nameIndex;
  }

  return mapping;
}

/** Splits a single "Name" column the way `guessColumnMapping` assumes it will be split. */
export function splitFullName(value: string): { firstName: string; lastName: string } {
  const trimmed = value.trim().replace(/\s+/g, ' ');
  const lastSpace = trimmed.lastIndexOf(' ');

  if (lastSpace === -1) return { firstName: trimmed, lastName: '' };
  return { firstName: trimmed.slice(0, lastSpace), lastName: trimmed.slice(lastSpace + 1) };
}

/** Recognizable display labels; mapping keys remain zero-based column positions. */
export function rosterColumnLabels(parsed: ParsedCsv, firstRowIsHeader: boolean): string[] {
  const examples = firstRowIsHeader ? parsed.headers : (parsed.rows[0] ?? []);
  return parsed.headers.map((_, index) => {
    const text = examples[index]?.trim();
    if (!text) return `Column ${index + 1}`;
    return examples.filter(value => value.trim() === text).length > 1 ? `${text} (${index + 1})` : text;
  });
}
